import { create } from 'zustand';
import { commentService } from '@/services/commentService';
import { showToast } from '@/lib/toast';
import {
    CommentWithMovie,
    CommentFilters,
} from '@/types/comment';

interface CommentState {
    // State
    comments: CommentWithMovie[];
    selectedComment: CommentWithMovie | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    isLoading: boolean;
    isLoadingDetail: boolean;
    isApproving: boolean;
    isRejecting: boolean;
    isDeleting: boolean;
    error: string | null;

    // Filters
    currentFilters: CommentFilters;

    // Actions
    fetchComments: (filters?: CommentFilters) => Promise<void>;
    fetchCommentById: (id: string) => Promise<void>;
    approveComment: (id: string) => Promise<void>;
    rejectComment: (id: string) => Promise<void>;
    deleteComment: (id: string) => Promise<void>;
    setSelectedComment: (comment: CommentWithMovie | null) => void;
    clearSelectedComment: () => void;
    setFilters: (filters: CommentFilters) => void;
    clearError: () => void;
    setCurrentPage: (page: number) => void;
    resetComments: () => void;
}

export const useCommentStore = create<CommentState>((set, get) => ({
    // Initial state
    comments: [],
    selectedComment: null,
    pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
    },
    isLoading: false,
    isLoadingDetail: false,
    isApproving: false,
    isRejecting: false,
    isDeleting: false,
    error: null,
    currentFilters: {},

    // Fetch comments with movie details
    fetchComments: async (filters?: CommentFilters) => {
        set({ isLoading: true, error: null });
        try {
            const currentFilters = filters || get().currentFilters;
            const response: any = await commentService.getCommentsWithMovieDetails(currentFilters);

            if (response && response.data) {
                set({
                    comments: response?.data?.data,
                    pagination: {
                        page: response?.data?.page || 1,
                        limit: response?.data?.limit || 10,
                        total: response?.data?.total || 0,
                        totalPages: response?.data?.totalPages || 0,
                    },
                    currentFilters,
                    isLoading: false,
                });
            } else {
                set({
                    error: "Lỗi khi tải danh sách bình luận",
                    isLoading: false,
                });
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi tải danh sách bình luận';
            set({ error: errorMessage, isLoading: false });
            showToast.error("Lỗi", errorMessage);
        }
    },

    // Fetch comment by ID
    fetchCommentById: async (id: string) => {
        set({ isLoadingDetail: true, error: null });
        try {
            // Tìm comment trong danh sách hiện tại
            const currentComments = get().comments;
            const comment = currentComments.find(c => c._id === id);

            if (comment) {
                set({
                    selectedComment: comment,
                    isLoadingDetail: false,
                });
            } else {
                set({
                    error: "Không tìm thấy bình luận",
                    isLoadingDetail: false,
                });
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi tải chi tiết bình luận';
            set({ error: errorMessage, isLoadingDetail: false });
            showToast.error("Lỗi", errorMessage);
        }
    },

    // Approve comment
    approveComment: async (id: string) => {
        set({ isApproving: true, error: null });
        try {
            await commentService.approveComment(id);

            // Update local state
            set((state) => ({
                comments: state.comments.map((comment) =>
                    comment._id === id ? { ...comment, status: 'approved' as const } : comment
                ),
                isApproving: false,
            }));

            showToast.success("Duyệt bình luận thành công");
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi duyệt bình luận';
            set({ error: errorMessage, isApproving: false });
            showToast.error("Lỗi", errorMessage);
            throw error;
        }
    },

    // Reject comment
    rejectComment: async (id: string) => {
        set({ isRejecting: true, error: null });
        try {
            await commentService.rejectComment(id);

            // Update local state
            set((state) => ({
                comments: state.comments.map((comment) =>
                    comment._id === id ? { ...comment, status: 'rejected' as const } : comment
                ),
                isRejecting: false,
            }));

            showToast.success("Từ chối bình luận thành công");
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi từ chối bình luận';
            set({ error: errorMessage, isRejecting: false });
            showToast.error("Lỗi", errorMessage);
            throw error;
        }
    },

    // Delete comment
    deleteComment: async (id: string) => {
        set({ isDeleting: true, error: null });
        try {
            await commentService.deleteComment(id);

            // Remove from local state
            set((state) => ({
                comments: state.comments.filter((comment) => comment._id !== id),
                pagination: {
                    ...state.pagination,
                    total: state.pagination.total - 1,
                },
                isDeleting: false,
            }));

            showToast.success("Xóa bình luận thành công");
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi xóa bình luận';
            set({ error: errorMessage, isDeleting: false });
            showToast.error("Lỗi", errorMessage);
            throw error;
        }
    },

    // Set selected comment
    setSelectedComment: (comment: CommentWithMovie | null) => {
        set({ selectedComment: comment });
    },

    // Clear selected comment
    clearSelectedComment: () => {
        set({ selectedComment: null });
    },

    // Set filters
    setFilters: (filters: CommentFilters) => {
        set({ currentFilters: filters });
    },

    // Clear error
    clearError: () => set({ error: null }),

    // Set current page
    setCurrentPage: (page: number) => {
        set((state) => ({
            pagination: { ...state.pagination, page },
        }));
    },

    // Reset comments
    resetComments: () => set({
        comments: [],
        selectedComment: null,
        pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
        },
        currentFilters: {},
        error: null,
    }),
}));
